import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { searchFlights } from "./fhpService";
import {
  dealsFromSearchFlightsSettled,
  dealsListFromSearchFlightsResponseBody,
  isSearchFlightsAxiosResponseOk,
  parallelRoundTripSettledTransportOk,
} from "../utils/flightParallelSearchHelpers";

dayjs.extend(customParseFormat);

/** Closest date shifts first; stop after first offset with any deals (limits load). */
const PROBE_OFFSETS_DAYS = [-1, 1, -2, 2, -3, 3];

/**
 * @param {string} dateStr DD/MM/YYYY from SearchContext
 * @param {number} deltaDays
 * @returns {string|null}
 */
export function shiftSearchDateDdMmYyyy(dateStr, deltaDays) {
  const d = dayjs(String(dateStr).trim(), "DD/MM/YYYY", true);
  if (!d.isValid()) return null;
  return d.add(deltaDays, "day").format("DD/MM/YYYY");
}

/**
 * Round-trip: same offset on outbound + return (fixed nights). Sequential waves, stop on first hit.
 */
function optionalAgentPayload(agentId) {
  return agentId != null && String(agentId).trim() !== ""
    ? { agent_id: String(agentId).trim() }
    : {};
}

export async function probeRoundTripFlightAlternatives({
  originToken,
  destToken,
  depDate,
  retDate,
  roomCompositions,
  isStale,
  agentId,
}) {
  const ag = optionalAgentPayload(agentId);
  /** @type {{ depDate: string, retDate: string, offsetDays: number }[]} */
  const suggestions = [];
  for (const off of PROBE_OFFSETS_DAYS) {
    if (isStale?.()) return suggestions;
    const d1 = shiftSearchDateDdMmYyyy(depDate, off);
    const d2 = shiftSearchDateDdMmYyyy(retDate, off);
    if (!d1 || !d2) continue;

    const settled = await Promise.allSettled([
      searchFlights({
        dealType: "RoundTrip",
        origin: originToken,
        destination: destToken,
        dep_date: d1,
        ret_date: d2,
        room_compositions: roomCompositions,
        ...ag,
      }),
      searchFlights({
        dealType: "OneWay",
        origin: originToken,
        destination: destToken,
        dep_date: d1,
        room_compositions: roomCompositions,
        ...ag,
      }),
      searchFlights({
        dealType: "OneWay",
        origin: destToken,
        destination: originToken,
        dep_date: d2,
        room_compositions: roomCompositions,
        ...ag,
      }),
    ]);

    if (!parallelRoundTripSettledTransportOk(settled)) continue;

    const rt = dealsFromSearchFlightsSettled(settled[0], "alt-rt");
    const out = dealsFromSearchFlightsSettled(settled[1], "alt-out");
    const inD = dealsFromSearchFlightsSettled(settled[2], "alt-in");
    const hasAny = rt.length > 0 || out.length > 0 || inD.length > 0;
    if (hasAny) {
      suggestions.push({ depDate: d1, retDate: d2, offsetDays: off });
      break;
    }
  }
  return suggestions;
}

/**
 * One-way: shift departure only.
 */
export async function probeOneWayFlightAlternatives({
  originToken,
  destToken,
  depDate,
  roomCompositions,
  isStale,
  agentId,
}) {
  const ag = optionalAgentPayload(agentId);
  /** @type {{ depDate: string, retDate: null, offsetDays: number }[]} */
  const suggestions = [];
  for (const off of PROBE_OFFSETS_DAYS) {
    if (isStale?.()) return suggestions;
    const d1 = shiftSearchDateDdMmYyyy(depDate, off);
    if (!d1) continue;
    const res = await searchFlights({
      dealType: "OneWay",
      origin: originToken,
      destination: destToken,
      dep_date: d1,
      room_compositions: roomCompositions,
      ...ag,
    });
    if (!isSearchFlightsAxiosResponseOk(res)) continue;
    const list = dealsListFromSearchFlightsResponseBody(res?.data);
    if (list.length > 0) {
      suggestions.push({ depDate: d1, retDate: null, offsetDays: off });
      break;
    }
  }
  return suggestions;
}
